import React, {useCallback, useEffect, useState} from 'react';
import {List, Map, Set} from 'immutable';
import {CallCardHeader} from '@components/call-card/CallCardHeader';
import {CallSpeakerOverlays} from '@components/call-card/CallSpeakerOverlays';
import {CallSpeakerSection} from '@components/call-card/CallSpeakerSection';
import {CallCardTabs} from '@components/call-card/CallCardTabs';
import {CallCardSidebar} from '@components/call-card/CallCardSidebar';
import {Call} from '@store/call-card/Call';
import {Topic, TopicFilter, TopicId} from '@store/topic-keyword/TopicKeyword.types';
import Scorecard from 'records/Scorecard';
import {CommentSection} from '@components/call-card/CommentSection';
import {Color, PctDouble, Seconds} from '@store/@types/util';
import User, {UserId} from 'records/User';
import './CallCard.sass';
import './CallCard.mobile.sass';
import {UserCallActivity} from '@store/call-card/UserCallActivity';
import {
  CallAsset,
  DragStatus,
  Section,
  Segment,
  Speaker,
  TopicMatch,
  TranscriptBlock,
} from '@store/call-card/CallCard.types';
import {RouterLocation} from '@store/@types/util/ReactRouter';
import {TagData} from 'stores/files/FileUploadStore';
import {Library} from '@store/@types/records/Library';
import {
  getPlayerProps,
  PlayerRef,
  registerPlayerEvents,
  registerSharedAreaEvents,
  registerTranscriptScrollEvents,
} from './Player.utils';
import {CallMediaPlayer} from '@components/call-card/CallMediaPlayer';
import {UserComment} from 'records/UserComment';
import {registerListenEvents} from '@components/call-card/registerListenEvents';
import {CallCardVariant} from '@components/call-card/CallCard.types';
import {combineMatchesByLine} from '@components/call-card/TranscriptMatch.utils';
import {CallCardSource} from '@store/call-card/CallCardSource';
import {CommentEditDialog} from '@components/call-card/CommentEditDialog';
import {SharedAreas} from '@components/call-card/SharedHighlights.utils';

interface CallCardProps {
  call: Call;
  speakers: List<Speaker>;
  sections: List<Section>;
  scorecards: List<Scorecard>;
  userActivities: List<UserCallActivity>;
  transcript: List<TranscriptBlock>;
  callAsset: CallAsset | undefined;
  talkTime: Map<number, Seconds>;
  segments: List<Segment>;
  comments: Map<UserId, List<UserComment>>;
  sessionUser: User;
  variant: CallCardVariant;
  sharedAreas: SharedAreas;

  matches: Map<TopicId, List<TopicMatch>>;
  activeTopics: Map<TopicId, Color>;
  topics: List<Topic>;
  supportsCrmSync: boolean;

  directReports: Set<UserId>;

  // Legacy component props
  location: RouterLocation;
  libraries: Library[];
  tags: List<TagData>;

  topicFilter: TopicFilter;
}

export const CallCard: React.FC<CallCardProps> = ({
  call,
  topics,
  scorecards,
  userActivities,
  callAsset,
  activeTopics,
  speakers,
  sections,
  transcript,
  talkTime,
  segments,
  location,
  sessionUser,
  libraries,
  tags,
  comments,
  variant,
  matches,
  supportsCrmSync,
  topicFilter,
  directReports,
  sharedAreas,
}) => {
  const [playerRef, setPlayerRef] = useState<PlayerRef>(null);
  const [playerProps, setPlayerProps] = useState(getPlayerProps(playerRef, call));
  const [dragStatus, setDragStatus] = useState<DragStatus>({
    isDragging: false,
    startPos: null,
    endPos: null,
  });
  const [newComment, setNewComment] = useState<UserComment>();
  const [autoScrollTranscript, setAutoScrollTranscript] = useState(true);
  const setTimePct = useCallback(
    (pct: PctDouble) => {
      if (playerRef) {
        playerRef.currentTime = pct * call.duration;
      }
    },
    [call, playerRef]
  );
  const setTimeSeconds = useCallback(
    time => {
      if (playerRef) {
        playerRef.currentTime = time;
      }
    },
    [playerRef]
  );
  const playRecording = useCallback(() => {
    if (playerRef) {
      playerRef.play();
    }
  }, [playerRef]);

  useEffect(() => registerListenEvents(playerRef, call.id, call.duration), [call, playerRef]);
  useEffect(() => {
    if (!autoScrollTranscript) return;
    return registerTranscriptScrollEvents(
      playerRef,
      transcript,
      combineMatchesByLine(matches, activeTopics)
    );
  }, [playerRef, transcript, matches, activeTopics, autoScrollTranscript]);
  useEffect(
    () => registerPlayerEvents(playerRef, call, setPlayerProps),
    [call, playerRef, setPlayerProps]
  );
  useEffect(() => registerSharedAreaEvents(playerRef, sharedAreas), [playerRef, sharedAreas]);

  return (
    <div className="new-call-card-root">
      <div className="call-card-main-content">
        <CallCardHeader
          call={call}
          scorecards={scorecards}
          comments={comments}
          sessionUser={sessionUser}
          variant={variant}
          supportsCrmSync={supportsCrmSync}
        />
        <CallMediaPlayer callAsset={callAsset} setPlayerRef={setPlayerRef} />
        <CallSpeakerOverlays
          sharedAreas={sharedAreas}
          callId={call.id}
          sections={sections}
          setDragStatus={setDragStatus}
          dragStatus={dragStatus}
          setNewComment={setNewComment}
          userId={sessionUser.id}
          {...playerProps}
        />
        <CallSpeakerSection
          sessionUser={sessionUser}
          speakers={speakers}
          talkTime={talkTime}
          segments={segments}
          setTimePct={setTimePct}
          activeTopics={activeTopics}
          matches={matches}
          callerId={call.caller.id}
          dragStatus={dragStatus}
          setDragStatus={setDragStatus}
          {...playerProps}
        />
        <CommentSection
          call={call}
          comments={comments}
          sessionUser={sessionUser}
          callAsset={callAsset}
          dragStatus={dragStatus}
          newComment={newComment}
          setNewComment={setNewComment}
          directReports={directReports}
          setTimeSeconds={setTimeSeconds}
          playRecording={playRecording}
          {...playerProps}
        />
        <CallCardTabs
          sessionUser={sessionUser}
          location={location}
          scorecards={scorecards}
          userActivities={userActivities}
          call={call}
          libraries={libraries}
          tags={tags}
          comments={comments.get(sessionUser.id, List())}
        />
      </div>
      <CallCardSidebar
        topics={topics}
        matches={matches}
        activeTopics={activeTopics}
        callId={call.id}
        keywordSearch={CallCardSource.keywordSearch}
        transcript={transcript}
        speakers={speakers}
        sessionUser={sessionUser}
        setTimeSeconds={setTimeSeconds}
        topicFilter={topicFilter}
        variant={variant}
        callState={call.state}
        autoScrollTranscript={autoScrollTranscript}
        setAutoScrollTranscript={setAutoScrollTranscript}
        {...playerProps}
      />
      {newComment && (
        <CommentEditDialog
          call={call}
          initialComment={newComment}
          callAsset={callAsset}
          onClose={() => {
            setNewComment(undefined);
          }}
          dragStatus={dragStatus}
          setDragStatus={setDragStatus}
        />
      )}
    </div>
  );
};
